import { hash } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { t } from 'ember-intl';
import { and, not, or } from 'ember-truth-helpers';
import { OTB_DEFAULT_VALUE } from 'frontend-utils/utils/constants/constant-values';
import GeneralTooltipsTooltipOtb from 'lumen/components/general/tooltips/tooltip-otb';
import HotelRatesTablesCellsTdBase from 'lumen/components/hotel/rates/tables/cells/td-base';
import changeColorClass from 'lumen/helpers/change-color-class';
import hasFeature from 'lumen/helpers/has-feature';
import { COMPARETYPES } from 'lumen/utils/constants/general-constants';
import { DETAIL_TABS } from 'lumen/utils/constants/rate-constants';

export default class HotelRatesTablesCellsTdOtb extends HotelRatesTablesCellsTdBase {
  DETAIL_TABS = DETAIL_TABS;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedOccupancyValue() {
    return this.occupancy?.formattedOccupancyPercentage;
  }

  get isMissing(): boolean {
    return (
      this.occupancy?.roomsBooked === OTB_DEFAULT_VALUE &&
      this.occupancy?.roomsBookedGroup === OTB_DEFAULT_VALUE &&
      this.occupancy?.roomsBookedIndividual === OTB_DEFAULT_VALUE &&
      this.occupancy?.totalCapacity === OTB_DEFAULT_VALUE
    );
  }

  get layoutConditionalClassNames(): string {
    const { layout, textAlign } = this.args.column;
    const alignment = textAlign === 'left' ? 'items-start' : 'items-end';
    const justify = textAlign === 'left' ? 'justify-start' : 'justify-end';
    const isRowLayout = layout === 'side-by-side';

    return isRowLayout
      ? `td-otb--row ${justify}`
      : `td-otb--column ${alignment}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get occupancyChange() {
    if (this.compareValue) {
      return this.occupancy?.occupancyChangeForExtractDate(this.compareValue);
    }
    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get occupancyChangeValue() {
    if (this.showOccupancyChanges) {
      return this.occupancy?.occupancyChangeVersusPercentage(
        this.occupancyChange,
      );
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get occupancyChangeValueFormatted() {
    if (
      this.showOccupancyChanges &&
      this.occupancyChangeValue &&
      Math.abs(this.occupancyChangeValue) >= 0.005
    ) {
      return this.intl.formatNumber(this.occupancyChangeValue, {
        style: 'percent',
        signDisplay: 'exceptZero',
      });
    }
    return '';
  }

  get showOccupancyChanges(): boolean {
    return (
      this.compareType === COMPARETYPES.DATE && this.compareValue !== undefined
    );
  }

  <template>
    <div class="text-200--regular mr-500 tabular-nums">
      {{#if
        (or
          (hasFeature "otb.empty_placeholder")
          (hasFeature "otb.data_validation")
          this.isMissing
        )
      }}
        <span
          class={{if
            (hasFeature "otb.empty_placeholder")
            "text-brand font-bold"
          }}
        >
          {{t "general.missing"}}
        </span>
      {{else}}
        <div class={{this.layoutConditionalClassNames}}>
          <div
            class="truncate
              {{if this.isPast 'td-past'}}
              {{if this.isToday 'td-today'}}
              {{if this.showOccupancyChanges 'td-left-value'}}"
          >
            <LinkTo
              @query={{hash
                date=this.formattedDate
                type=this.DETAIL_TABS.BOOKING_CURVE
              }}
              @route="hotel.rates.detail"
              class="truncate"
            >
              {{this.formattedOccupancyValue}}
            </LinkTo>
          </div>
          <div>
            {{#if
              (and this.showOccupancyChanges this.occupancyChangeValueFormatted)
            }}
              <div
                class="td-right-value
                  {{changeColorClass
                    this.occupancyChangeValue
                    threshold=0.005
                  }}"
              >
                {{this.occupancyChangeValueFormatted}}
              </div>
            {{else if this.compareType}}
              &nbsp;
            {{/if}}
          </div>
        </div>

        <EmberTooltip
          @delay={{25}}
          @side="right"
          @tooltipClass="base-tooltip tooltip-otb-container"
        >
          {{! @glint-expect-error: Incorrect type }}
          <GeneralTooltipsTooltipOtb
            @occupancy={{this.occupancy}}
            @referenceOccupancy={{this.occupancyChange}}
            @showAvailableRooms={{true}}
            @showChangesRow={{true}}
            @showUpdateTime={{not this.occupancy.linkedDayIsPast}}
          />
        </EmberTooltip>
      {{/if}}
    </div>
  </template>
}
